/**
 * One wall tile: a bare `<img>` inside a focusable button. Deliberately NOT
 * antd `Image` — there is no modal lightbox on the wall (the strip IS the
 * zoom), so a bare element satisfies `preview={false}` by construction and
 * costs nothing.
 */
import { type CSSProperties, memo, useCallback, useState } from "react"
import styled from "styled-components"

import { useArtworkSrc } from "#hooks/newHooks/useRenditionUrl"
import { type RenditionTier } from "#utils/artworkRenditionHelpers"

import { WALL_ACCENT, WALL_TILE_BG } from "./artworkWallTheme"
import { type ArtworkWallItem } from "./artworkWallTypes"

export type TileLayout = "square" | "full" | "strip"

interface ArtworkTileProps {
  item: ArtworkWallItem
  index: number
  layout: TileLayout
  tier: RenditionTier
  /** > 1 renders the "Latest · N versions" badge. */
  versionCount?: number
  isActive?: boolean
  onClick: (item: ArtworkWallItem, index: number) => void
  /**
   * The tile's box just changed shape because its image decoded with an
   * aspect other than the one it was laid out with. Fires only when the
   * aspect actually changed, so an image that matches its remembered size
   * (the common case) stays silent.
   */
  onGeometryChange?: (item: ArtworkWallItem) => void
}

const STile = styled.button<{ $layout: TileLayout }>`
  all: unset;
  box-sizing: border-box;
  position: relative;
  display: block;
  cursor: pointer;
  background: ${WALL_TILE_BG};
  overflow: hidden;

  ${({ $layout }) =>
    $layout === "square"
      ? /* Off-screen tiles skip layout + paint; the intrinsic size keeps the
           scrollbar honest before they are ever rendered. */
        `width: 100%; aspect-ratio: 1 / 1;
         content-visibility: auto; contain-intrinsic-size: auto 240px;`
      : $layout === "full"
        ? `width: 100%;
           content-visibility: auto; contain-intrinsic-size: auto 240px;`
        : /* NO content-visibility here: a skipped strip tile would be laid out
             at the 240px placeholder instead of its real aspect-derived
             width, and every tile left of the centred one contributes to its
             scroll offset. The strip holds at most one page of tiles. */
          `height: 100%; flex: 0 0 auto; scroll-snap-align: center;`}

  &:hover,
  &:focus-visible {
    outline: 2px solid ${WALL_ACCENT};
    outline-offset: -2px;
  }
`

const SBadges = styled.div`
  position: absolute;
  left: 0;
  bottom: 0;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
  pointer-events: none;
`

const SBadge = styled.span`
  padding: 2px 6px;
  font-size: 11px;
  line-height: 16px;
  color: #111;
  background: ${WALL_ACCENT};
`

interface NaturalSize {
  width: number
  height: number
}

/**
 * Natural sizes decoded this session, by wall item id. Entering the strip
 * remounts every tile under a new parent, so without this an aspect-true
 * tile's box would collapse to 0 wide until its image (re)loads — and the
 * strip centres on boxes. Thumb, med and original all share an aspect
 * (renditions scale uniformly), so a size learnt from the grid's
 * `_thumb.jpg` sizes the strip's `_med.jpg` box before a byte of it arrives.
 * A mobile `thumbnailUrl` may not match; the box corrects itself on load
 * and reports the change through `onGeometryChange`.
 */
const rememberedNaturalSizes = new Map<string, NaturalSize>()

export const resetRememberedNaturalSizesForTests = (): void =>
  rememberedNaturalSizes.clear()

/**
 * A strip tile whose image has never decoded is laid out square so it has a
 * real width for its neighbours' scroll offsets; it snaps to the true aspect
 * on load. Never used in the grid, where the square box is the layout.
 */
const STRIP_PLACEHOLDER_SIZE: NaturalSize = { width: 1, height: 1 }

const ASPECT_EPSILON = 0.005

const sameAspect = (a: NaturalSize, b: NaturalSize): boolean =>
  Math.abs(a.width / a.height - b.width / b.height) < ASPECT_EPSILON

const isQuarterTurn = (rotation: number): boolean =>
  ((rotation % 360) + 360) % 360 === 90 ||
  ((rotation % 360) + 360) % 360 === 270

/**
 * `rotation` is applied at display time, never baked into pixels (the
 * scanner and customer apps depend on that). Square tiles cover-crop, so a
 * quarter turn still fills the box. Aspect-true layouts need the box to take
 * the ROTATED aspect, which is only known once the image has loaded (or was
 * remembered from an earlier load).
 *
 * Strip boxes get an explicit `aspect-ratio` rather than letting the `<img>`
 * dictate the width, so the box is right from the first layout and does not
 * jump when the tier swaps `src`.
 */
const getImageStyles = (
  layout: TileLayout,
  rotation: number,
  natural: NaturalSize | null
): { wrapper: CSSProperties; img: CSSProperties } => {
  const rotate = rotation ? `rotate(${rotation}deg)` : undefined

  if (layout === "square") {
    return {
      wrapper: { width: "100%", height: "100%" },
      img: {
        width: "100%",
        height: "100%",
        objectFit: "cover",
        transform: rotate,
      },
    }
  }

  const fillsWidth = layout === "full"

  if (isQuarterTurn(rotation) && natural) {
    const { width: w, height: h } = natural
    // Box takes the rotated aspect (h/w); the img keeps its own aspect but is
    // sized to the box's cross axis, then turned about the centre.
    return {
      wrapper: {
        position: "relative",
        aspectRatio: `${h} / ${w}`,
        ...(fillsWidth ? { width: "100%" } : { height: "100%" }),
      },
      img: {
        position: "absolute",
        top: "50%",
        left: "50%",
        width: `calc(100% * ${w / h})`,
        aspectRatio: `${w} / ${h}`,
        height: "auto",
        transform: `translate(-50%, -50%) ${rotate ?? ""}`,
      },
    }
  }

  if (fillsWidth) {
    return {
      wrapper: { width: "100%" },
      img: {
        width: "100%",
        height: "auto",
        display: "block",
        transform: rotate,
      },
    }
  }

  // Strip: the box owns the aspect; the img just fills it. A square
  // placeholder stands in until the first decode (rotation is moot on it).
  const { width: w, height: h } = natural ?? STRIP_PLACEHOLDER_SIZE
  return {
    wrapper: { height: "100%", aspectRatio: `${w} / ${h}` },
    img: {
      width: "100%",
      height: "100%",
      objectFit: "contain",
      display: "block",
      transform: rotate,
    },
  }
}

const ArtworkTile = ({
  item,
  index,
  layout,
  tier,
  versionCount = 1,
  isActive = false,
  onClick,
  onGeometryChange,
}: ArtworkTileProps) => {
  const src = useArtworkSrc(item.artwork, tier)
  const [natural, setNatural] = useState<NaturalSize | null>(
    () => rememberedNaturalSizes.get(item.id) ?? null
  )
  const rotation = item.artwork.rotation ?? 0

  const handleLoad = useCallback(
    (event: React.SyntheticEvent<HTMLImageElement>) => {
      const { naturalWidth, naturalHeight } = event.currentTarget
      if (naturalWidth <= 0 || naturalHeight <= 0) return
      const size = { width: naturalWidth, height: naturalHeight }
      rememberedNaturalSizes.set(item.id, size)
      if (natural && sameAspect(natural, size)) return
      setNatural(size)
      onGeometryChange?.(item)
    },
    [item, natural, onGeometryChange]
  )

  const handleClick = useCallback(
    () => onClick(item, index),
    [index, item, onClick]
  )

  const styles = getImageStyles(layout, rotation, natural)
  const caption = item.caption ?? "Student artwork"

  return (
    <STile
      type="button"
      $layout={layout}
      data-testid="artwork-tile"
      data-artwork-id={item.id}
      data-active={isActive || undefined}
      aria-label={caption}
      aria-current={isActive || undefined}
      onClick={handleClick}
    >
      <div style={styles.wrapper}>
        <img
          src={src}
          alt={caption}
          loading="lazy"
          decoding="async"
          draggable={false}
          onLoad={handleLoad}
          style={styles.img}
        />
      </div>
      {versionCount > 1 || item.badge ? (
        <SBadges>
          {versionCount > 1 ? (
            <SBadge data-testid="version-badge">
              Latest · {versionCount} versions
            </SBadge>
          ) : null}
          {item.badge ? (
            <SBadge data-testid="item-badge">{item.badge}</SBadge>
          ) : null}
        </SBadges>
      ) : null}
    </STile>
  )
}

export default memo(ArtworkTile)
